import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Mail, Send, Users, Plus, FileText, Layers, UserCheck, MailPlus, ClipboardCheck } from 'lucide-react';
import { listCampaigns, listSegments, fetchContactStats } from '../../lib/supabase';
import { StatusBadge } from '../../components/StatusBadge';
import { PageHeader } from '../../components/PageHeader';
import { formatDateTime, formatNumber } from '../../lib/format';
import { useAuthStore } from '../../stores/authStore';
import { useFreigabenStore } from '../../stores/freigabenStore';
import { istRedaktion } from '../../lib/rollen';
import './Dashboard.css';

function getGreeting(fullName: string): string {
  const hour = new Date().getHours();
  const name = fullName ? `, ${fullName.split(' ')[0]}` : '';
  if (hour < 12) return `Guten Morgen${name}`;
  if (hour < 17) return `Guten Tag${name}`;
  return `Guten Abend${name}`;
}

export function Dashboard() {
  const { user } = useAuthStore();
  const canEdit = user?.role === 'admin' || user?.role === 'redakteur';
  const wartend = useFreigabenStore((s) => s.anzahl);

  const campaignsQuery = useQuery({ queryKey: ['campaigns'], queryFn: listCampaigns, refetchOnMount: 'always' });
  const segmentsQuery  = useQuery({ queryKey: ['segments'],  queryFn: listSegments,  refetchOnMount: 'always' });
  const statsQuery     = useQuery({ queryKey: ['contact-stats'], queryFn: fetchContactStats, refetchOnMount: 'always' });

  const campaigns = campaignsQuery.data ?? [];
  const segments  = segmentsQuery.data ?? [];
  const contactStats = statsQuery.data;

  const stats = {
    totalCampaigns: campaigns.length,
    sent: campaigns.filter(c => c.status === 'sent').length,
    drafts: campaigns.filter(c => c.status === 'draft').length,
    scheduled: campaigns.filter(c => c.status === 'scheduled').length,
  };

  const recent = campaigns.slice(0, 5);

  return (
    <div className="dashboard">
      <PageHeader
        title={getGreeting(user?.fullName ?? '')}
        subtitle="Überblick über aktuelle Kampagnen, Gruppen und Kontakte"
        actions={canEdit ? (
          <>
            <Link to="/neue-mail" className="btn btn-secondary"><MailPlus size={16} /> Neue Mail (Assistent)</Link>
            <Link to="/campaigns/new" className="btn btn-primary"><Plus size={16} /> Neue Kampagne</Link>
          </>
        ) : null}
      />

      {istRedaktion(user) && wartend > 0 && (
        <Link to="/freigaben" className="alert alert-warning dash-freigaben" data-tour="dash-freigaben">
          <ClipboardCheck size={16} />
          <div>
            <strong>{wartend} {wartend === 1 ? 'Mail wartet' : 'Mails warten'} auf die Freigabe</strong> — von Geschäftsführung oder Studioleitung. Zu den Freigaben →
          </div>
        </Link>
      )}

      <div className="kpi-grid" data-tour="dash-kpis">
        <div className="kpi-card">
          <p className="kpi-label">Kampagnen gesamt</p>
          <div className="kpi-value">{formatNumber(stats.totalCampaigns)}</div>
          <p className="kpi-hint">{formatNumber(stats.drafts)} Entwürfe · {formatNumber(stats.scheduled)} geplant</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Versendet</p>
          <div className="kpi-value">{formatNumber(stats.sent)}</div>
          <p className="kpi-hint">erfolgreich abgeschlossen</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Gruppen</p>
          <div className="kpi-value">{formatNumber(segments.length)}</div>
          <p className="kpi-hint">davon {formatNumber(segments.filter(s => s.is_dynamic).length)} dynamisch</p>
        </div>
        <div className="kpi-card">
          <p className="kpi-label">Kontakte (Opt-In)</p>
          <div className="kpi-value">{statsQuery.isLoading ? '…' : formatNumber(contactStats?.optIn ?? 0)}</div>
          <p className="kpi-hint">von {formatNumber(contactStats?.total ?? 0)} insgesamt</p>
        </div>
      </div>

      <div className="dashboard-grid">
        <div className="card" data-tour="dash-liste">
          <div className="card-header">
            <h2 className="card-title">Letzte Kampagnen</h2>
            <Link to="/campaigns" className="btn btn-ghost btn-sm">Alle anzeigen</Link>
          </div>

          {campaignsQuery.isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}><div className="loading-spinner" /></div>
          ) : recent.length === 0 ? (
            <div className="empty-state">
              <Mail size={48} />
              <h3>Noch keine Kampagnen</h3>
              <p>Legen Sie Ihre erste Kampagne an, um loszulegen.</p>
              {canEdit && (<div style={{ marginTop: '1rem' }}><Link to="/campaigns/new" className="btn btn-primary"><Plus size={16} /> Erste Kampagne</Link></div>)}
            </div>
          ) : (
            <table className="data-table">
              <thead><tr><th>Name</th><th>Status</th><th>Versendet / Geplant</th></tr></thead>
              <tbody>
                {recent.map(c => (
                  <tr key={c.id} className="clickable">
                    <td>
                      <Link to={`/campaigns/${c.id}`} style={{ color: 'inherit', textDecoration: 'none', fontWeight: 500 }}>{c.name}</Link>
                      <div style={{ fontSize: '0.8125rem', color: 'var(--text-tertiary)' }}>{c.subject}</div>
                    </td>
                    <td><StatusBadge status={c.status} /></td>
                    <td style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                      {c.sent_at ? formatDateTime(c.sent_at) : c.scheduled_at ? formatDateTime(c.scheduled_at) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="dashboard-side">
          <div className="card">
            <div className="card-header"><h2 className="card-title">Schnellzugriffe</h2></div>
            <div className="quick-links">
              <Link to="/campaigns" className="quick-link">
                <div className="quick-link-icon" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}><Send size={20} /></div>
                <div><div className="quick-link-title">Kampagnen</div><div className="quick-link-sub">Erstellen und versenden</div></div>
              </Link>
              <Link to="/segments" className="quick-link">
                <div className="quick-link-icon" style={{ background: 'var(--info-light)', color: 'var(--info-dark)' }}><Layers size={20} /></div>
                <div><div className="quick-link-title">Gruppen</div><div className="quick-link-sub">Empfänger definieren</div></div>
              </Link>
              <Link to="/templates" className="quick-link">
                <div className="quick-link-icon" style={{ background: 'var(--warning-light)', color: 'var(--warning-dark)' }}><FileText size={20} /></div>
                <div><div className="quick-link-title">Templates</div><div className="quick-link-sub">Vorlagen verwalten</div></div>
              </Link>
              <Link to="/contacts" className="quick-link">
                <div className="quick-link-icon" style={{ background: 'var(--success-light)', color: 'var(--success-dark)' }}><UserCheck size={20} /></div>
                <div><div className="quick-link-title">Kontakte</div><div className="quick-link-sub">Aus CRM gespiegelt</div></div>
              </Link>
            </div>
          </div>

          <div className="card">
            <div className="card-header"><h2 className="card-title">Kontakt-Sync</h2></div>
            {contactStats ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}><span style={{ color: 'var(--text-secondary)' }}>Gesamt</span><strong>{formatNumber(contactStats.total)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}><span style={{ color: 'var(--text-secondary)' }}>Mit Marketing-Opt-In</span><strong style={{ color: 'var(--success-dark)' }}>{formatNumber(contactStats.optIn)}</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-secondary)' }}>Ohne Opt-In / abgemeldet</span><strong style={{ color: 'var(--danger-dark)' }}>{formatNumber(contactStats.total - contactStats.optIn)}</strong></div>
              </div>
            ) : (<div className="loading-spinner" style={{ margin: '1rem auto' }} />)}
          </div>

          <div className="alert alert-info"><Users size={16} /><div><strong>Gut zu wissen:</strong> Kontakte werden jede Nacht automatisch aus dem CRM synchronisiert. Änderungen erscheinen am nächsten Morgen.</div></div>
        </div>
      </div>
    </div>
  );
}
